/**
 * useStampedPreview — S2-T2.4.
 *
 * Runs the stamping engine over the loaded PDF + config and returns the
 * stamped bytes for the preview. Debounced so rapid config edits (typing a
 * prefix, dragging the opacity slider) coalesce into one engine run; stale and
 * in-flight runs are cancelled so the preview always reflects the latest
 * config. Moving the engine off the main thread is S2-T2.5; this hook keeps
 * the same shape so that swap is internal.
 */

import { useEffect, useState } from "react";

import type { StampingConfig } from "../../engine/models";
import type { LoadedPdf } from "../components/DropZone";
import { stampDocument } from "../engine-client";

export type StampStatus = "idle" | "stamping" | "ready" | "error";

interface Options {
  delayMs?: number;
}

export function useStampedPreview(
  loaded: LoadedPdf | null,
  config: StampingConfig,
  { delayMs = 200 }: Options = {},
): { stamped: Uint8Array | null; status: StampStatus } {
  const [stamped, setStamped] = useState<Uint8Array | null>(null);
  const [status, setStatus] = useState<StampStatus>("idle");

  useEffect(() => {
    if (!loaded) {
      setStamped(null);
      setStatus("idle");
      return;
    }

    let cancelled = false;
    setStatus("stamping");

    const timer = setTimeout(() => {
      void stampDocument(loaded, config)
        .then((bytes) => {
          if (cancelled) return;
          setStamped(bytes);
          setStatus(bytes ? "ready" : "error");
        })
        .catch(() => {
          if (!cancelled) setStatus("error");
        });
    }, delayMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [loaded, config, delayMs]);

  return { stamped, status };
}
